import { ChevronDownIcon, FileText } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandCollection,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { notify } from "@/lib/notify";
import { cn } from "@/lib/utils";

import type { ClaimWithRelations } from "../../api/use-claim";
import {
  useClaimAcquisitions,
  useCompetentCourts,
  useCreateOnePager,
  useInitClaimProcedure,
} from "../../api/use-claim-actions";
import { getProcedureTypeLabel, PROCEDURE_TYPE_OPTIONS, type ProcedureType } from "../../lib/procedure-types";
import { CtaShell } from "./cta-shell";
import { ExhibitsOrderPre } from "./legal-subpoena-cta";

interface CourtOption {
  label: string;
  value: number | null;
}

/** Port of ui/select/SelectCompetentCourt.vue — suggested competent courts first, then all. */
export function SelectCompetentCourt({
  claimId,
  value,
  onChange,
}: {
  claimId: string;
  value: number | null;
  onChange: (courtId: number | null) => void;
}) {
  const { data: courts } = useCompetentCourts(claimId);
  const [open, setOpen] = useState(false);

  const options: CourtOption[] = [
    { label: "Aucun choix sélectionné", value: null },
    { label: `${courts?.airline?.name || "Aucun"} (Siège compagnie)`, value: courts?.airline?.id ?? null },
    {
      label: `${courts?.departureAirport?.name || "Aucun"} (Lieu Départ)`,
      value: courts?.departureAirport?.id ?? null,
    },
    {
      label: `${courts?.arrivalAirport?.name || "Aucun"} (Lieu d'Arrivée)`,
      value: courts?.arrivalAirport?.id ?? null,
    },
    ...(courts?.all ?? []).map((court) => ({ label: court.name, value: court.id })),
  ];

  const selected = options.find((option) => option.value === value && value !== null) ?? null;

  return (
    <div>
      <Label className="mb-1 block text-sm font-medium text-gray-700">Tribunal compétent</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          className={cn(
            "flex w-full items-center justify-between gap-x-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm",
            selected ? "text-gray-900" : "text-gray-500",
          )}
        >
          <span className="truncate">{selected?.label ?? "Aucun choix sélectionné"}</span>
          <ChevronDownIcon className="size-4 shrink-0 text-gray-400" aria-hidden="true" />
        </PopoverTrigger>
        <PopoverContent align="start" className="w-(--anchor-width) min-w-56 p-0">
          <Command items={options} itemToStringValue={(option) => (option as CourtOption).label}>
            <CommandInput placeholder="Rechercher un tribunal" />
            <CommandEmpty>Pas de résultat.</CommandEmpty>
            <CommandList>
              <CommandCollection>
                {(option: CourtOption) => (
                  <CommandItem
                    key={`${option.label}-${option.value}`}
                    value={option}
                    onClick={() => {
                      onChange(option.value);
                      setOpen(false);
                    }}
                  >
                    {option.label}
                  </CommandItem>
                )}
              </CommandCollection>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

/** Port of states-cta/InitProcedureCTA.vue — initialisation de la procédure. */
export function InitProcedureCTA({ claim }: { claim: ClaimWithRelations }) {
  const initClaimProcedure = useInitClaimProcedure(claim.id);
  const createOnePager = useCreateOnePager(claim.id);
  const { data: claimAcquisitions } = useClaimAcquisitions(claim.id);

  const [selectedCourt, setSelectedCourt] = useState<number | null>(null);
  const [selectedProcedureType, setSelectedProcedureType] = useState<ProcedureType>("PROCEDURE_CLASSIQUE");
  const [selectedFamilyClaimAcquisitionIds, setSelectedFamilyClaimAcquisitionIds] = useState<string[]>([]);
  const [isMediationOnePager, setIsMediationOnePager] = useState(true);

  function toggleFamilyAcquisition(id: string) {
    setSelectedFamilyClaimAcquisitionIds((ids) =>
      ids.includes(id) ? ids.filter((existingId) => existingId !== id) : [...ids, id],
    );
  }

  function initProcedure() {
    if (!selectedCourt) {
      notify.info("Veuillez sélectionner un tribunal");
      return;
    }
    initClaimProcedure.mutate({
      juridictionId: selectedCourt,
      procedureType: selectedProcedureType || "PROCEDURE_CLASSIQUE",
      familyClaimAcquisitionsIds: selectedFamilyClaimAcquisitionIds,
    });
  }

  return (
    <CtaShell legalStepName={claim.claimLegalStep.name} title="Action requise">
      <p className="mt-2 text-sm text-gray-500">
        Initialiser la procédure, pour pouvoir ensuite générer l'assignation.
      </p>

      <div className="relative block text-sm">
        <div className="rounded border-2 border-dotted p-4">
          <div className="my-3 mt-4 py-2">
            <div>
              <Label className="mb-1 block text-sm font-medium text-gray-700">Type de procédure</Label>
              <Select
                value={selectedProcedureType}
                onValueChange={(value) => {
                  if (value) {
                    setSelectedProcedureType(value as ProcedureType);
                  }
                }}
              >
                <SelectTrigger className="w-full bg-white">
                  <SelectValue>{getProcedureTypeLabel(selectedProcedureType)}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {PROCEDURE_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="mt-2">
              <SelectCompetentCourt claimId={claim.id} value={selectedCourt} onChange={setSelectedCourt} />
            </div>

            {/* Family Claim Acquisitions Selection */}
            {claimAcquisitions?.families?.length ? (
              <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-3">
                <p className="mb-2 text-sm font-medium text-gray-700">
                  Sélectionner les déclarations passager à inclure :
                </p>
                <div className="space-y-2">
                  {claimAcquisitions.families.map((acquisition) => (
                    <Label
                      key={acquisition.id}
                      className="flex cursor-pointer items-start gap-3 rounded-md border bg-white p-3 hover:bg-gray-50"
                    >
                      <Checkbox
                        checked={selectedFamilyClaimAcquisitionIds.includes(acquisition.id)}
                        onCheckedChange={() => toggleFamilyAcquisition(acquisition.id)}
                        className="mt-0.5"
                      />
                      <div className="flex-1">
                        <span className="font-medium text-gray-900">
                          Notes passager(s) de la famille{" "}
                          {acquisition.family?.familyName || acquisition.toEmail}
                        </span>
                        {acquisition.passengerNote ? (
                          <p className="mt-1 text-sm text-gray-500">{acquisition.passengerNote}</p>
                        ) : null}
                      </div>
                    </Label>
                  ))}
                </div>
              </div>
            ) : null}

            <p className="mt-4 text-sm leading-6 text-gray-500">
              <span className="leading-8 font-semibold text-gray-900">
                Préparer le Projet d'Assignation
                <br />
              </span>
              <span className="font-semibold text-slate-600">
                Bien vérifier les demandes (Article 5, 9, 14.2, et demandes supplémentaires).
              </span>
            </p>

            <ExhibitsOrderPre />

            <div>
              <Button onClick={initProcedure} disabled={initClaimProcedure.isPending}>
                <FileText />
                Initialiser la procédure
              </Button>
            </div>
          </div>
        </div>
      </div>

      {claim.claimState.type === "COURT.WAIT_FOR_JURIDCTION_CHOICE" ? (
        <div className="my-6 rounded-md border border-blue-200 bg-blue-50 px-4 py-6">
          <h2 className="mb-4 text-base leading-6 font-medium text-blue-900">Générer le One-Pager</h2>
          <p className="mb-4 text-sm text-blue-700">
            Le One-Pager médiation se génère dès l'entrée en contentieux ; le One-Pager classique, après avoir
            initialisé la procédure (choix de la juridiction).
          </p>
          <Label className="mb-3 flex cursor-pointer items-center gap-2">
            <Checkbox
              checked={isMediationOnePager}
              onCheckedChange={(checked) => setIsMediationOnePager(checked === true)}
            />
            <span className="text-sm font-medium text-blue-900">Dossier de médiation</span>
          </Label>
          <Button
            disabled={(!isMediationOnePager && !claim.procedure) || createOnePager.isPending}
            onClick={() => createOnePager.mutate({ isMediation: isMediationOnePager })}
          >
            Générer le One-Pager
          </Button>
        </div>
      ) : null}
    </CtaShell>
  );
}
